import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { LinkTemplatesService } from '../../../core/services/link-templates.service';
import { selectAccountUuid } from '../user/user.selectors';
import * as LinkTemplatesActions from './link-templates.actions';
import * as LinkTemplatesSelectors from './link-templates.selectors';

@Injectable()
export class LinkTemplatesEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private linkTemplatesService = inject(LinkTemplatesService);

  loadTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkTemplatesActions.loadTemplates),
      switchMap((query) =>
        this.linkTemplatesService.getTemplates(query).pipe(
          map((response) =>
            LinkTemplatesActions.loadTemplatesSuccess({
              templates: response.data,
              meta: response.meta,
            })
          ),
          catchError((error) =>
            of(
              LinkTemplatesActions.loadTemplatesFailure({
                error: error?.message ?? 'Failed to load link templates',
              })
            )
          )
        )
      )
    )
  );

  loadTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkTemplatesActions.loadTemplate),
      switchMap(({ uuid }) =>
        this.linkTemplatesService.getTemplateById(uuid).pipe(
          map((template) => LinkTemplatesActions.loadTemplateSuccess({ template })),
          catchError((error) =>
            of(
              LinkTemplatesActions.loadTemplateFailure({
                error: error?.message ?? 'Failed to load link template',
              })
            )
          )
        )
      )
    )
  );

  exportTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkTemplatesActions.exportTemplates),
      switchMap(({ payload }) =>
        this.linkTemplatesService.exportTemplates(payload).pipe(
          map((response) =>
            LinkTemplatesActions.exportTemplatesSuccess({
              message: response.message ?? 'Your export request is being processed.',
            })
          ),
          catchError((error) =>
            of(
              LinkTemplatesActions.exportTemplatesFailure({
                error: error?.message ?? 'Something went wrong while processing an export.',
              })
            )
          )
        )
      )
    )
  );

  createTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkTemplatesActions.createTemplate),
      switchMap(({ payload }) =>
        this.linkTemplatesService.createTemplate(payload).pipe(
          map((template) => LinkTemplatesActions.createTemplateSuccess({ template })),
          catchError((error) =>
            of(LinkTemplatesActions.mutationFailure({
              error: error?.message ?? 'Failed to create link template',
            }))
          )
        )
      )
    )
  );

  updateTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkTemplatesActions.updateTemplate),
      switchMap(({ uuid, payload }) =>
        this.linkTemplatesService.updateTemplate(uuid, payload).pipe(
          map((template) => LinkTemplatesActions.updateTemplateSuccess({ template })),
          catchError((error) =>
            of(LinkTemplatesActions.mutationFailure({
              error: error?.message ?? 'Failed to update link template',
            }))
          )
        )
      )
    )
  );

  deleteTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkTemplatesActions.deleteTemplate),
      switchMap(({ uuid }) =>
        this.linkTemplatesService.deleteTemplate(uuid).pipe(
          map(() => LinkTemplatesActions.deleteTemplateSuccess({ uuid })),
          catchError((error) =>
            of(LinkTemplatesActions.mutationFailure({
              error: error?.message ?? 'Failed to delete link template',
            }))
          )
        )
      )
    )
  );

  setDefaultTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkTemplatesActions.setDefaultTemplate),
      withLatestFrom(this.store.select(selectAccountUuid)),
      switchMap(([{ uuid }, accountUuid]) => {
        if (!accountUuid) {
          return of(LinkTemplatesActions.mutationFailure({ error: 'Account UUID is required' }));
        }

        return this.linkTemplatesService.setDefaultTemplate(accountUuid, uuid).pipe(
          map(() => LinkTemplatesActions.setDefaultTemplateSuccess()),
          catchError((error) =>
            of(LinkTemplatesActions.mutationFailure({
              error: error?.message ?? 'Failed to set the default link template',
            }))
          )
        );
      })
    )
  );

  reloadAfterMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LinkTemplatesActions.createTemplateSuccess,
        LinkTemplatesActions.updateTemplateSuccess,
        LinkTemplatesActions.deleteTemplateSuccess
      ),
      withLatestFrom(this.store.select(LinkTemplatesSelectors.selectLinkTemplatesLastQuery)),
      map(([, query]) => LinkTemplatesActions.loadTemplates(query))
    )
  );

}
